import { useCallback, useEffect, useState } from 'react';
import type { AcademicLedgerEntry, LedgerSummary } from '@starter/shared';
import { api, ApiError } from '../../api/client.js';
import { EmptyState } from '../../components/EmptyState.js';
import { ErrorBanner } from '../../components/ErrorBanner.js';
import { LoadingState } from '../../components/LoadingState.js';
import { PageHeader } from '../../components/PageHeader.js';

export function CasesPage() {
  const [entries, setEntries] = useState<AcademicLedgerEntry[]>([]);
  const [summary, setSummary] = useState<LedgerSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    return Promise.all([api.getAcademicLedger(), api.getAcademicLedgerSummary()])
      .then(([ledgerRes, summaryRes]) => {
        setEntries(ledgerRes.data);
        setSummary(summaryRes.summary);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Failed to load academic ledger'));
  }, []);

  useEffect(() => {
    setLoading(true);
    load().finally(() => setLoading(false));
  }, [load]);

  return (
    <div className="page">
      <PageHeader title="Academic Ledger" subtitle="Faculty-approved cases that count toward graduation" />

      {error && <ErrorBanner message={error} onRetry={() => void load()} />}

      {summary && (
        <div className="ledger-summary-grid">
          <div className="card stat-card">
            <p className="stat-card__label">Total Approved Cases</p>
            <p className="stat-card__value">{summary.totalApprovedCases}</p>
          </div>
          {Object.entries(summary.countsBySpecialty).map(([specialty, count]) => (
            <div key={specialty} className="card stat-card">
              <p className="stat-card__label">{specialty}</p>
              <p className="stat-card__value">{count}</p>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <LoadingState message="Loading approved cases…" />
      ) : entries.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={<span aria-hidden>🏥</span>}
            title="No approved cases yet"
            description="Approved cases from faculty-reviewed clinical sheets will appear here with procedure, specialty, and role details."
          />
        </div>
      ) : (
        <div className="card">
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>Approved</th>
                  <th>Case Date</th>
                  <th>Procedure</th>
                  <th>Specialty</th>
                  <th>Role</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry) => (
                  <tr key={entry.id}>
                    <td>{new Date(entry.approvedAt).toLocaleDateString()}</td>
                    <td>{entry.caseDate ?? '—'}</td>
                    <td>{entry.procedure}</td>
                    <td>{entry.specialty ?? '—'}</td>
                    <td>{entry.caseRole?.replace(/_/g, ' ') ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
